import React, { useState } from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { BrowserRouter as Router, Switch, Route, useRouteMatch, Link, Redirect } from 'react-router-dom';
import clsx from 'clsx';
import Cookies from 'js-cookie';
import { readAccessToken } from '@emarketer/auth';
import BrandingWrapper from './Components/common/BrandingWrapper';
import {
  Typography,
  CssBaseline,
  Drawer,
  ListItemText,
  ListItemIcon,
  List,
  ListItem,
  IconButton,
  Divider,
} from '@material-ui/core';
import {
  AccountTree as AccountTreeIcon,
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  FormatListNumbered as FormatListNumberedIcon,
  BubbleChart as StatsIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@material-ui/icons';
import AllMetrics from './Components/AllMetrics/AllMetrics';
import MetricGrowth from './Components/MetricGrowth/MetricGrowth';
import Metric from './Components/Metric/Metric';
import LockedMetric from './Components/LockedMetric/LockedMetric.js';
import Summary from './Components/Summary/Summary';
import BrowseByTaxonomy from './Components/BrowseByTaxonomy/BrowseByTaxonomy';
import BrowseByDemographic from './Components/BrowseByDemographic/BrowseByDemographic';
import Health from './Components/common/Health';
import LoadRemoteComponent from './Components/LoadRemoteComponent/LoadRemoteComponent';
import config from './config';
import { checkForecastPlusAccess } from './helpers/AuthMethods';
import { Helmet } from 'react-helmet';

const drawerWidth = 240;

const styles = (theme) => ({
  root: {
    display: 'flex',
  },
  toolbar: {
    paddingRight: 24,
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: '#383838',
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginLeft: 12,
    marginRight: 36,
  },
  menuButtonHidden: {
    display: 'none',
  },
  title: {
    flexGrow: 1,
    fontWeight: theme.typography.weight.bold,
    fontSize: '28px',
    backgroundColor: '#383838',
    color: 'white',
    minHeight: '64px',
    padding: '10px 10px 10px 10px',
  },
  drawerPaper: {
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    zIndex: 999999,
  },
  drawerPaperClose: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    overflow: 'auto',
  },
  active: {
    backgroundColor: 'rgba(0, 0, 0, 0.14)',
  },
  loginBox: {
    overflowX: 'auto',
    margin: 'auto',
    maxWidth: 550,
    padding: theme.spacing(5),
  },
  container: {
    padding: theme.spacing(3),
    minHeight: '100vh',
  },
});

const useStyles = makeStyles(styles);

const makeAuth =
  (isAuthed) =>
  (WrappedComponent, lockedRedirectRoute, noRoleNeeded = false) =>
  (props) => {
    const classes = useStyles();
    let authenticated = false;
    let hasPremiumForecastAccess = false;
    const cookie = Cookies.get(config.authCookieName);

    if (isAuthed && cookie) {
      const userAccessToken = readAccessToken(cookie);
      authenticated = userAccessToken.authenticated;
      hasPremiumForecastAccess = checkForecastPlusAccess();
    }
    // Check if User has Role Access
    if (authenticated && (hasPremiumForecastAccess || noRoleNeeded)) {
      return <WrappedComponent {...props} />;
    }
    // If user doesn't have role access redirect to Locked Component if any
    if (authenticated && lockedRedirectRoute) {
      if (lockedRedirectRoute === '/lockedmetric') {
        return <Redirect to={{ pathname: lockedRedirectRoute + window.location.pathname.split('metric')[1] }} />;
      }
      return <Redirect to={{ pathname: lockedRedirectRoute }} />;
    }
    // TODO: Redirect users to the Marketing page
    else {
      return (
        <div className={classes.loginBox}>
          <LoadRemoteComponent
            url={config.loginComponentUrl}
            name="loginorsignupcomponent-inline"
            props={{ returnToCurrentServiceAfterRegistration: true }}
          >
            {null}
          </LoadRemoteComponent>
        </div>
      );
    }
  };

const MenuLink = (props) => {
  const { path, icon, text } = props;
  const match = !!useRouteMatch(path);

  return (
    <ListItem button to={path} component={Link} selected={match}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={text} />
    </ListItem>
  );
};

const App = (props) => {
  const [open, setOpen] = useState(true);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const { classes, isAuthed } = props;
  const withAuth = makeAuth(isAuthed);

  return (
    <Router basename={config.basename}>
      <>
        <CssBaseline />
        <Helmet>
          <script src={config.analyticsUrl}></script>
          <script type="text/javascript" src={config.osanoScriptUrl}></script>
          <script type="text/javascript" src={config.osfScriptUrl}></script>
          <link rel="preload" href={config.fontCssUrl} as="style" />
        </Helmet>
        <BrandingWrapper>
          <div className={classes.root}>
            {config.beastMode ? (
              <Drawer
                variant="permanent"
                classes={{
                  paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
                }}
                open={open}
              >
                <div className={clsx(classes.toolbarIcon, !open && classes.menuButtonHidden)}>
                  <IconButton onClick={handleDrawerClose}>
                    <ChevronLeftIcon />
                  </IconButton>
                </div>
                <div className={clsx(classes.toolbarIcon, open && classes.menuButtonHidden)}>
                  <IconButton color="inherit" aria-label="Open drawer" onClick={handleDrawerOpen}>
                    <MenuIcon />
                  </IconButton>
                </div>
                <Divider />
                <List>
                  <div>
                    {config.enableTaxonomyPage && (
                      <MenuLink path="/taxonomy" icon={<AccountTreeIcon />} text="Taxonomy" />
                    )}
                    {config.enableDemographicsPage && (
                      <MenuLink path="/demographics" icon={<PeopleIcon />} text="Demographics" />
                    )}
                    {config.enableGrowthPage && (
                      <MenuLink path="/growth" icon={<EventNoteIcon />} text="Explore by Year" />
                    )}
                    <MenuLink path="/metrics" icon={<FormatListNumberedIcon />} text="All Metrics" />
                    {config.enableStatsPage && <MenuLink path="/stats" icon={<StatsIcon />} text="System Stats" />}
                  </div>
                </List>
              </Drawer>
            ) : null}
            <main className={classes.content}>
              <Typography
                component="div"
                className={clsx(classes.appContainer, classes.container)}
                style={{ position: 'relative' }}
              >
                <Switch>
                  <Route path="/metrics" component={withAuth(AllMetrics)} />
                  <Route path="/metric/:metricIds" component={withAuth(Metric, '/lockedmetric')} />
                  <Route path="/lockedmetric/:metricIds" component={withAuth(LockedMetric, null, true)} />
                  <Route path="/growth" component={withAuth(MetricGrowth)} />
                  <Route path="/taxonomy/:ids?" component={withAuth(BrowseByTaxonomy)} />
                  <Route path="/demographics/:ids?" component={withAuth(BrowseByDemographic)} />
                  <Route path="/stats" component={withAuth(Summary)} />
                  <Route path="/client-side-health" component={Health} />
                  <Route path="/" component={withAuth(AllMetrics, null, true)} />
                </Switch>
              </Typography>
            </main>
          </div>
        </BrandingWrapper>
      </>
    </Router>
  );
};

export default withStyles(styles)(App);
